import {
  Atom,
  Biohazard,
  BookOpen,
  Briefcase,
  Calculator,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EXAM_LANGUAGE_FLAGS,
  ROLE_LABEL_KEYS,
  SAMPLE_EXAMS,
  type SampleExamSubjectKey,
  type SampleFileRole,
} from '../../../../lib/sample-exams.js';
import { cn } from '../../../../lib/utils.js';

const SUBJECT_ICONS: Record<SampleExamSubjectKey, React.ComponentType<{ className?: string }>> = {
  math: Calculator,
  english: BookOpen,
  technical: Briefcase,
  biology: Biohazard,
  physics: Atom,
  french: BookOpen,
  spanish: BookOpen,
  political_education: BookOpen,
  german: BookOpen,
};

const SUBJECT_COLORS: Record<SampleExamSubjectKey, { text: string; bg: string }> = {
  math: { text: 'text-blue-500', bg: 'bg-blue-50' },
  biology: { text: 'text-green-500', bg: 'bg-green-50' },
  technical: { text: 'text-amber-500', bg: 'bg-amber-50' },
  english: { text: 'text-purple-500', bg: 'bg-purple-50' },
  physics: { text: 'text-cyan-500', bg: 'bg-cyan-50' },
  french: { text: 'text-pink-500', bg: 'bg-pink-50' },
  spanish: { text: 'text-yellow-500', bg: 'bg-yellow-50' },
  political_education: { text: 'text-slate-500', bg: 'bg-slate-50' },
  german: { text: 'text-indigo-500', bg: 'bg-indigo-50' },
};

const ROLE_BADGE_COLORS: Record<SampleFileRole, string> = {
  submission: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
  masterSolution: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  gradingScheme: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  blankExam: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
};

export function DemoSelector({
  onSelectDemo,
  loadingId,
}: {
  onSelectDemo: (examId: string) => void;
  loadingId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const userLang = i18n.language.split('-')[0];
  const visibleExams = SAMPLE_EXAMS.filter(
    e => !e.examLanguage || e.examLanguage === 'en' || e.examLanguage === userLang,
  );
  const visibleSubjects = [...new Set(visibleExams.map(e => e.subjectKey))];
  const [expandedSubject, setExpandedSubject] = useState<string | null>(visibleSubjects[0] ?? null);

  return (
    <div className="flex flex-col gap-3 pb-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">{t('Canvas.demoSelector.title')}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('Canvas.demoSelector.description')}</p>
      </div>

      {visibleSubjects.map(subject => {
        const isExpanded = expandedSubject === subject;
        const Icon = SUBJECT_ICONS[subject] ?? BookOpen;
        const colors = SUBJECT_COLORS[subject] ?? { text: 'text-slate-500', bg: 'bg-slate-50' };
        const examsForSubject = visibleExams.filter(e => e.subjectKey === subject);

        return (
          <div
            key={subject}
            className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-card shadow-sm"
          >
            <button
              onClick={() => setExpandedSubject(isExpanded ? null : subject)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', colors.bg)}>
                  <Icon className={cn('w-5 h-5', colors.text)} />
                </div>
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {t(`Canvas.demoSelector.subjects.${subject}`)}
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {t('Canvas.demoSelector.examCount', { count: examsForSubject.length })}
                </span>
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-slate-400 dark:text-slate-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400 dark:text-slate-500" />
              )}
            </button>

            {isExpanded && (
              <div className="bg-slate-50 dark:bg-secondary/50 border-t border-slate-100 dark:border-slate-700 p-3 flex flex-col gap-2">
                {examsForSubject.map(exam => {
                  const isLoading = loadingId === exam.id;
                  return (
                    <button
                      key={exam.id}
                      onClick={() => !loadingId && onSelectDemo(exam.id)}
                      disabled={!!loadingId}
                      className="flex flex-col text-left p-3 rounded-lg border border-transparent dark:border-slate-700/50 bg-white dark:bg-card shadow-sm hover:border-brand-500 hover:shadow-md transition-all group disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <div className="flex justify-between items-start mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-500">
                            {t(exam.gradeKey)}
                          </span>
                          {exam.examLanguage && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800/20 text-slate-600 dark:text-slate-300">
                              {EXAM_LANGUAGE_FLAGS[exam.examLanguage]}
                            </span>
                          )}
                        </div>
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-brand-500 transition-colors" />
                        )}
                      </div>
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{t(exam.nameKey)}</span>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {exam.files.map(f => (
                          <span
                            key={f.role}
                            className={cn(
                              'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                              ROLE_BADGE_COLORS[f.role],
                            )}
                          >
                            {t(ROLE_LABEL_KEYS[f.role])}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
